import React, { useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import { Tooltip, IconButton, Button } from '@chakra-ui/react';
import { FiFileText } from 'react-icons/fi';
import LogoColegio from '../../assets/img/logoColegio.png';
import FirmaRector from '../../assets/img/firmaRector.png';
import { useDispatch, useSelector } from 'react-redux';
import { getAllNotasByStudent } from '../../features/notaSlice';
import globalInformation from '../../helpers/globalInformation';
import { CustomToast } from '../../helpers/toast';

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = API_URL && API_URL.split('/api')[0];

// Estilos mejorados con diseño profesional y esquema de colores verde
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#FFFFFF',
    fontSize: 10,
  },

  // Encabezado institucional
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#27ae60',
    paddingBottom: 12,
  },
  logoContainer: {
    width: 60,
    height: 60,
    marginRight: 15,
  },
  schoolInfoContainer: {
    flex: 1,
  },
  schoolName: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  schoolDecrees: {
    fontSize: 7,
    color: '#2c3e50',
    marginBottom: 1,
  },
  schoolCode: {
    fontSize: 8,
    fontWeight: 600,
    color: '#2c3e50',
    marginTop: 2,
  },

  // Información del estudiante en 3 columnas
  studentContainer: {
    flexDirection: 'row',
    padding: 12,
    backgroundColor: '#f0f9f4',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#d5e8dd',
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
    marginBottom: 20,
  },
  studentInfoColumn: {
    width: '33.33%',
    paddingHorizontal: 5,
  },
  studentInfoLabel: {
    fontSize: 7,
    fontWeight: 700,
    color: '#27ae60',
    marginBottom: 2,
    textTransform: 'uppercase',
  },
  studentInfoValue: {
    fontSize: 9,
    color: '#2c3e50',
    marginBottom: 6,
    fontWeight: 600,
  },

  // Tabla de evaluación
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1.5,
    borderColor: '#27ae60',
    borderRadius: 4,
    marginBottom: 25,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#27ae60',
    borderBottomWidth: 2,
    borderBottomColor: '#1e8449',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eaecee',
  },
  tableHeaderCell: {
    padding: 8,
    fontSize: 10,
    fontWeight: 700,
    textAlign: 'center',
    color: '#ffffff',
    borderRightWidth: 1,
    borderRightColor: '#1e8449',
    letterSpacing: 0.5,
  },
  tableCell: {
    padding: 6,
    fontSize: 8,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#eaecee',
    justifyContent: 'center',
  },
  subjectNameCell: {
    padding: 12,
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1.5,
    borderRightColor: '#27ae60',
    backgroundColor: '#e8f6ed',
  },
  periodRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#d5e8dd',
    minHeight: 28,
  },
  periodCell: {
    padding: 7,
    fontSize: 8,
    fontWeight: 700,
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#d5e8dd',
    backgroundColor: '#f8fcfa',
    justifyContent: 'center',
  },
  valoracionCell: {
    padding: 8,
    fontSize: 8,
    textAlign: 'left',
    lineHeight: 1.4,
    color: '#2c3e50',
  },
  noValoracion: {
    color: '#95a5a6',
    fontStyle: 'italic',
  },
  evenRow: {
    backgroundColor: '#fafdfb',
  },
  subjectDivider: {
    borderBottomWidth: 1.5,
    borderBottomColor: '#27ae60',
    marginBottom: 0,
  },

  // Sección de firmas
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 40,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#d5e8dd',
  },
  signature: {
    width: 200,
    textAlign: 'center',
  },
  signatureLine: {
    borderTopWidth: 1.5,
    borderTopColor: '#27ae60',
    marginBottom: 6,
    marginTop: 8,
  },
  signatureText: {
    fontSize: 8,
    color: '#2c3e50',
    textAlign: 'center',
    marginTop: 2,
  },
  signatureImage: {
    width: 120,
    height: 60,
    marginBottom: 2,
    alignSelf: 'center',
  },

  // Pie de página
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  footerText: {
    fontSize: 7,
    color: '#7f8c8d',
  },
});

// Función para formatear texto con viñetas
const FormattedText = ({ text }) => {
  if (!text) return null;

  // Dividir por saltos de línea y filtrar líneas vacías
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

  return (
    <View>
      {lines.map((line, index) => {
        const trimmedLine = line.trim();
        // Si empieza con *, • o -, es una viñeta
        const isBullet = /^[*\u2022-]/.test(trimmedLine);
        const cleanContent = isBullet ? trimmedLine.replace(/^[*\u2022-]\s*/, '') : trimmedLine;

        return (
          <View key={index} style={{ flexDirection: 'row', marginBottom: 2 }}>
            {isBullet && (
              <Text style={{ width: 10, fontSize: 8 }}>•</Text>
            )}
            <Text style={{ flex: 1, fontSize: 9, textAlign: 'justify', lineHeight: 1.3 }}>
              {cleanContent}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// Componente para el reporte preescolar
const AcademicReportPDF = ({ studentData, evaluaciones, schoolInfo, sede }) => {
  // Extraer información del estudiante
  const student = studentData?.estudiante || {};
  const academicYear = studentData?.academic_year || '2025';
  const grade = studentData?.grado || {};

  const logoToUse = schoolInfo?.logoUrl || LogoColegio;
  const schoolNameToUse = schoolInfo?.name || globalInformation?.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Encabezado con información de la institución */}
        <View style={styles.headerContainer}>
          <View style={styles.logoContainer}>
            <Image src={logoToUse} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </View>
          <View style={styles.schoolInfoContainer}>
            <Text style={styles.schoolName}>
              {schoolNameToUse}
            </Text>
            <View style={{ marginBottom: 4 }}>
              {globalInformation?.decretos?.map((decreto, index) => (
                <Text key={index} style={styles.schoolDecrees}>
                  {decreto?.tipo} N° {decreto.numero} del {decreto.fecha}
                </Text>
              ))}
            </View>
            <Text style={styles.schoolCode}>SEDE: {sede?.nombre || 'EL ASERRÍO'}</Text>
            <Text style={styles.schoolCode}>
              CÓDIGO DANE: {sede?.codigoDane || '25480000058201'}
            </Text>
          </View>
          {/* Logo derecho opcional o escudo */}
          <View style={styles.logoContainer}>
            <Image src={logoToUse} style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.5 }} />
          </View>
        </View>

        {/* Título del reporte */}
        <View style={{ marginBottom: 15, borderBottomWidth: 1, borderBottomColor: '#27ae60', paddingBottom: 5 }}>
          <Text style={{ fontSize: 12, fontWeight: 700, textAlign: 'center', color: '#27ae60', textTransform: 'uppercase' }}>
            INFORME GENERAL EVALUATIVO - NIVEL {grade?.nivel || 'INICIAL'}
          </Text>
        </View>

        {/* Información del estudiante en 3 columnas */}
        <View style={styles.studentContainer}>
          <View style={styles.studentInfoColumn}>
            <Text style={styles.studentInfoLabel}>NOMBRES Y APELLIDOS:</Text>
            <Text style={styles.studentInfoValue}>
              {student?.nombres} {student?.apellidos}
            </Text>
            <Text style={styles.studentInfoLabel}>IDENTIFICACIÓN:</Text>
            <Text style={styles.studentInfoValue}>{student?.dni}</Text>
          </View>
          <View style={styles.studentInfoColumn}>
            <Text style={styles.studentInfoLabel}>GRADO Y NIVEL:</Text>
            <Text style={styles.studentInfoValue}>
              {grade?.nombre || 'JARDÍN'} - {grade?.nivel || 'INICIAL'}
            </Text>
            <Text style={styles.studentInfoLabel}>AÑO LECTIVO:</Text>
            <Text style={styles.studentInfoValue}>
              {academicYear?.year}
            </Text>
          </View>
          <View style={styles.studentInfoColumn}>
            <Text style={styles.studentInfoLabel}>JORNADA:</Text>
            <Text style={styles.studentInfoValue}>
              {student?.turno || 'MAÑANA'}
            </Text>
            <Text style={styles.studentInfoLabel}>
              INFORME
            </Text>
            <Text style={styles.studentInfoValue}>
              CONSOLIDADO
            </Text>
          </View>
        </View>

        {/* Tabla de experiencias y valoraciones por periodo */}
        <View style={styles.table}>
          {/* Encabezado de la tabla */}
          <View style={styles.tableHeaderRow}>
            <View style={[styles.tableHeaderCell, { width: '25%' }]}>
              <Text>DIMENSIONES / EXPERIENCIAS</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '10%' }]}>
              <Text>PER.</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '65%' }]}>
              <Text>VALORACIÓN INTEGRAL</Text>
            </View>
          </View>

          {/* Filas de experiencias con valoraciones por periodo */}
          {evaluaciones?.map((exp, index) => {
            // Contador para alternar colores entre materias
            const isEvenExp = index % 2 === 0;

            // Creamos una estructura para cada materia
            return (
              <View key={index} wrap={false} style={{ marginBottom: 5 }}>
                {/* Fila del nombre de la materia - que abarca los 4 periodos en altura */}
                <View
                  style={[styles.tableRow, isEvenExp ? styles.evenRow : null]}
                >
                  {/* Columna del nombre de la materia */}
                  <View style={[styles.subjectNameCell, { width: '25%' }]}>
                    <Text style={{ fontSize: 9, textTransform: 'uppercase' }}>{exp.nombre}</Text>
                  </View>

                  {/* Columna para las filas de periodos */}
                  <View style={{ width: '75%' }}>
                    {/* Primer periodo */}
                    <View style={styles.periodRow}>
                      <View style={[styles.periodCell, { width: '13.33%' }]}>
                        <Text>P. 1</Text>
                      </View>
                      <View style={[styles.valoracionCell, { width: '86.67%' }]}>
                        {exp.valoraciones?.find(val => val.periodo === 1) ? (
                          <FormattedText text={exp.valoraciones.find(val => val.periodo === 1).valoracion} />
                        ) : (
                          <Text style={styles.noValoracion}>—</Text>
                        )}
                      </View>
                    </View>

                    {/* Segundo periodo */}
                    <View style={styles.periodRow}>
                      <View style={[styles.periodCell, { width: '13.33%' }]}>
                        <Text>P. 2</Text>
                      </View>
                      <View style={[styles.valoracionCell, { width: '86.67%' }]}>
                        {exp.valoraciones?.find(val => val.periodo === 2) ? (
                          <FormattedText text={exp.valoraciones.find(val => val.periodo === 2).valoracion} />
                        ) : (
                          <Text style={styles.noValoracion}>—</Text>
                        )}
                      </View>
                    </View>

                    {/* Tercer periodo */}
                    <View style={styles.periodRow}>
                      <View style={[styles.periodCell, { width: '13.33%' }]}>
                        <Text>P. 3</Text>
                      </View>
                      <View style={[styles.valoracionCell, { width: '86.67%' }]}>
                        {exp.valoraciones?.find(val => val.periodo === 3) ? (
                          <FormattedText text={exp.valoraciones.find(val => val.periodo === 3).valoracion} />
                        ) : (
                          <Text style={styles.noValoracion}>—</Text>
                        )}
                      </View>
                    </View>

                    {/* Cuarto periodo */}
                    <View style={styles.periodRow}>
                      <View style={[styles.periodCell, { width: '13.33%' }]}>
                        <Text>P. 4</Text>
                      </View>
                      <View style={[styles.valoracionCell, { width: '86.67%' }]}>
                        {exp.valoraciones?.find(val => val.periodo === 4) ? (
                          <FormattedText text={exp.valoraciones.find(val => val.periodo === 4).valoracion} />
                        ) : (
                          <Text style={styles.noValoracion}>—</Text>
                        )}
                      </View>
                    </View>
                  </View>
                </View>

                {/* Separador entre materias */}
                {index < evaluaciones.length - 1 && (
                  <View style={styles.subjectDivider} />
                )}
              </View>
            );
          })}
        </View>

        {/* Sección de firmas con wrap={false} para evitar que se corte */}
        <View style={styles.signatureSection} wrap={false}>
          <View style={styles.signature}>
            <Image src={FirmaRector} style={styles.signatureImage} />
            <View style={styles.signatureLine}></View>
            <Text style={styles.signatureText}>RECTOR(A)</Text>
            <Text style={[styles.signatureText, { fontWeight: 700 }]}>
              {globalInformation?.rector || 'RAÚL JHONEDINSON MARTÍNEZ SUÁREZ'}
            </Text>
          </View>
          <View style={styles.signature}>
            <View style={{ height: 60 }} /> {/* Espacio para firma manual */}
            <View style={styles.signatureLine}></View>
            <Text style={styles.signatureText}>DOCENTE TITULAR</Text>
            <Text style={[styles.signatureText, { fontWeight: 700 }]}>
              {grade?.docente_titular?.nombre || 'CÉSAR AUGUSTO ACOSTA'}
            </Text>
          </View>
        </View>

        {/* Pie de página */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Este reporte es un documento oficial de la Institución Educativa.
            Generado el {new Date().toLocaleDateString()}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Componente botón para generar y descargar el reporte
const ReporteGeneralEstudianteInicialCalificacion = ({
  data,
  configuracion,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const dispatch = useDispatch();

  const { sedeSeleccionada, user } = useSelector(state => state.auth);

  const defaultSchoolInfo = {
    name: configuracion?.nombreColegio || globalInformation.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO',
    logoUrl: configuracion?.logo ? `${BASE_URL}/uploads/${configuracion.logo}` : globalInformation.logoColegio,
    address: globalInformation.direccionColegio,
    phone: globalInformation.telefonoColegio,
    email: globalInformation.emailColegio,
  };

  const generateAndDownloadPdf = async () => {
    if (!data?._id) {
      CustomToast({
        title: 'Error',
        message: 'No se encontraron datos del estudiante',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Obtener notas del estudiante
      const response = await dispatch(getAllNotasByStudent(data)).unwrap();
      const notas = response?.notas;

      // Validar que haya datos
      if (!notas || !notas.length) {
        throw new Error(
          'No se encontraron datos de calificaciones para este estudiante'
        );
      }

      // Procesar notas para incluir nombre de materia
      const evaluaciones = notas.map(nota => ({
        ...nota,
        nombre: nota.materia?.nombre || 'Sin nombre',
      }));

      // Generar PDF
      const contenido = (
        <AcademicReportPDF
          studentData={data}
          sede={sedeSeleccionada}
          evaluaciones={evaluaciones}
          schoolInfo={defaultSchoolInfo}
        />
      );

      // Crear blob y descargar
      const blob = await pdf(contenido).toBlob();
      const url = URL.createObjectURL(blob);

      const fileName = `boletin_${data?.estudiante?.nombres
        ?.toLowerCase()
        .replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      CustomToast({
        title: 'Éxito',
        message: 'Boletín académico generado y descargado correctamente',
        type: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      CustomToast({
        title: 'Error',
        message:
          error.message ||
          'Error al generar el boletín académico. Revise si el estudiante tiene evaluaciones registradas.',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Tooltip label="Generar boletín general" placement="auto">
      <Button
        colorScheme="pink"
        variant="outline"
        size="sm"
        isLoading={isGenerating}
        leftIcon={<FiFileText />}
        onClick={generateAndDownloadPdf}
        loadingText="Generando boletín..."
        isDisabled={
          user?.usuario?.rol !== 'ADMIN_ROLE' &&
          configuracion?.permitirDescargarBoletin === false
        }
      >
        General
      </Button>
    </Tooltip>
  );
};

export default ReporteGeneralEstudianteInicialCalificacion;
